import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { X, Play, RotateCcw } from 'lucide-react';

const INHALE_DURATION = 4; // secondes
const EXHALE_DURATION = 8; // secondes
const CYCLE_DURATION = INHALE_DURATION + EXHALE_DURATION; // 12 secondes
const TOTAL_DURATION = 90; // 1min30
const TOTAL_CYCLES = Math.floor(TOTAL_DURATION / CYCLE_DURATION); // 7 cycles

export const BreathingExercise = ({ onClose }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle', 'inhale', 'exhale', 'done'
  const [countdown, setCountdown] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const phaseStartTimeRef = useRef(null);

  const resetExercise = useCallback(() => {
    setIsRunning(false);
    setPhase('idle');
    setCountdown(0);
    setTotalElapsed(0);
    setCycleCount(0);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, []);

  const startExercise = () => {
    setIsRunning(true);
    setPhase('inhale');
    setCountdown(1); // Start at 1 for counting up
    setTotalElapsed(0);
    setCycleCount(1);
    startTimeRef.current = Date.now();
    phaseStartTimeRef.current = Date.now();
  };

  // Main animation loop using requestAnimationFrame for smooth updates
  useEffect(() => {
    if (!isRunning || phase === 'idle' || phase === 'done') return;

    let lastSecond = -1;

    const animate = () => {
      const now = Date.now();
      const totalMs = now - startTimeRef.current;
      const phaseMs = now - phaseStartTimeRef.current;
      
      // Update total elapsed (every second)
      const currentSecond = Math.floor(totalMs / 1000);
      if (currentSecond !== lastSecond) {
        lastSecond = currentSecond;
        setTotalElapsed(currentSecond);
        
        // Check if exercise is done
        if (currentSecond >= TOTAL_DURATION) {
          setPhase('done');
          setIsRunning(false);
          return;
        }
      }

      // Determine current phase based on time
      const currentPhaseDuration = phase === 'inhale' ? INHALE_DURATION : EXHALE_DURATION;
      const phaseSeconds = phaseMs / 1000;
      
      // Update counter (counting UP: 1, 2, 3, 4... instead of down)
      const elapsed = Math.floor(phaseSeconds) + 1;
      setCountdown(Math.min(elapsed, currentPhaseDuration));

      // Check phase transition
      if (phaseSeconds >= currentPhaseDuration) {
        phaseStartTimeRef.current = now;
        if (phase === 'inhale') {
          setPhase('exhale');
          setCountdown(1); // Start at 1 for counting up
        } else {
          // New cycle
          const newCycle = cycleCount + 1;
          if (newCycle > TOTAL_CYCLES) {
            setPhase('done');
            setIsRunning(false);
            return;
          }
          setCycleCount(newCycle);
          setPhase('inhale');
          setCountdown(1); // Start at 1 for counting up
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRunning, phase, cycleCount]);

  const getProgressPercent = () => {
    return Math.min(100, (totalElapsed / TOTAL_DURATION) * 100);
  };

  const getInstructionText = () => {
    if (phase === 'idle') return 'Pret a commencer ?';
    if (phase === 'inhale') return 'Inspirez...';
    if (phase === 'exhale') return 'Expirez...';
    if (phase === 'done') return 'Bravo !';
    return '';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // CSS animation classes for smooth bubble movement
  const getBubbleAnimation = () => {
    if (phase === 'inhale') {
      return 'animate-bubble-up';
    }
    if (phase === 'exhale') {
      return 'animate-bubble-down';
    }
    return '';
  };

  const modalContent = (
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 flex flex-col overflow-hidden"
      style={{ 
        zIndex: 99999,
        background: 'linear-gradient(to bottom, #0F4C4A, #0B3B39)'
      }}
    >
      {/* CSS Animations */}
      <style>{`
        @keyframes bubbleUp {
          0% {
            bottom: 20%;
            transform: translateX(-50%) scale(1);
          }
          100% {
            bottom: 70%;
            transform: translateX(-50%) scale(1.3);
          }
        }
        @keyframes bubbleDown {
          0% {
            bottom: 70%;
            transform: translateX(-50%) scale(1.3);
          }
          100% {
            bottom: 20%;
            transform: translateX(-50%) scale(1);
          }
        }
        .animate-bubble-up {
          animation: bubbleUp ${INHALE_DURATION}s ease-in-out forwards;
        }
        .animate-bubble-down {
          animation: bubbleDown ${EXHALE_DURATION}s ease-in-out forwards;
        }
        .animate-bubble-idle {
          bottom: 20%;
          transform: translateX(-50%) scale(1);
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between p-4 pt-6">
        <h2 className="text-white text-lg font-medium">Coherence cardiaque</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/10"
          data-testid="breathing-close"
        >
          <X className="w-6 h-6" />
        </Button>
      </div>

      {/* Progress bar */}
      {isRunning && (
        <div className="px-4">
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white/60"
              style={{ 
                width: `${getProgressPercent()}%`,
                transition: 'width 0.5s linear'
              }}
            />
          </div>
          <div className="flex justify-between mt-2 text-white/60 text-xs">
            <span>{formatTime(totalElapsed)}</span>
            <span>{formatTime(TOTAL_DURATION)}</span>
          </div>
        </div>
      )}

      {/* Main animation area */}
      <div className="flex-1 relative flex flex-col items-center justify-center px-6">
        {/* Instruction text */}
        <div className="absolute top-8 text-center">
          <p className={`text-2xl font-light transition-colors duration-500 ${
            phase === 'inhale' ? 'text-sky-300' : 
            phase === 'exhale' ? 'text-pink-300' : 
            'text-white'
          }`}>
            {getInstructionText()}
          </p>
          {(phase === 'inhale' || phase === 'exhale') && (
            <p className="text-6xl font-light text-white mt-4 tabular-nums">
              {countdown}
            </p>
          )}
        </div>

        {/* Bubble container */}
        <div className="relative w-full h-64">
          {/* Track line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-white/10 rounded-full" />
          
          {/* Top marker */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-1 bg-white/30 rounded-full" style={{ top: '30%' }} />
          
          {/* Bottom marker */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-1 bg-white/30 rounded-full" style={{ bottom: '20%' }} />
          
          {/* Bubble */}
          <div
            key={`${phase}-${cycleCount}`}
            className={`absolute left-1/2 w-20 h-20 rounded-full ${
              phase === 'inhale' ? 'bg-gradient-to-br from-sky-400 to-sky-600 shadow-lg shadow-sky-500/50' :
              phase === 'exhale' ? 'bg-gradient-to-br from-pink-400 to-pink-600 shadow-lg shadow-pink-500/50' :
              'bg-gradient-to-br from-white/40 to-white/20'
            } ${getBubbleAnimation()}`}
            style={phase === 'idle' || phase === 'done' ? {
              bottom: '20%',
              transform: 'translateX(-50%) scale(1)'
            } : undefined}
            data-testid="breathing-bubble"
          >
            {/* Inner glow */}
            <div className="absolute inset-2 rounded-full bg-white/30" />
          </div>
        </div>

        {/* Cycle counter */}
        {isRunning && (
          <div className="absolute bottom-8 text-center">
            <p className="text-white/60 text-sm">
              Cycle {cycleCount} / {TOTAL_CYCLES}
            </p>
          </div>
        )}

        {/* Done message */}
        {phase === 'done' && (
          <div className="absolute bottom-16 text-center">
            <p className="text-white/80 text-lg mb-2">
              Exercice termine
            </p>
            <p className="text-white/60 text-sm">
              Vous avez complete {TOTAL_CYCLES} cycles de respiration
            </p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-6 pb-10">
        {phase === 'idle' && (
          <Button
            onClick={startExercise}
            className="w-full h-14 bg-white text-sage-800 hover:bg-white/90 rounded-xl text-lg font-medium"
            data-testid="breathing-start"
          >
            <Play className="w-5 h-5 mr-2" />
            Commencer (1min30)
          </Button>
        )}

        {phase === 'done' && (
          <div className="space-y-3">
            <Button
              onClick={resetExercise}
              variant="outline"
              className="w-full h-14 border-white/30 text-white hover:bg-white/10 rounded-xl text-lg"
              data-testid="breathing-restart"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Recommencer
            </Button>
            <Button
              onClick={onClose}
              className="w-full h-14 bg-white text-sage-800 hover:bg-white/90 rounded-xl text-lg font-medium"
              data-testid="breathing-done"
            >
              Termine
            </Button>
          </div>
        )}

        {isRunning && (
          <p className="text-center text-white/40 text-sm">
            Respirez calmement au rythme de la bulle
          </p>
        )}
      </div>
    </div>
  );

  // Utiliser un portail pour rendre le modal dans le body
  return createPortal(modalContent, document.body);
};

export default BreathingExercise;
